import type { KeyboardEvent, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import copyJson from '../../demo/fixtures/copy.json'
import enumsJson from '../../demo/fixtures/enums.json'
import { formatInteger, formatMultiple, formatUsd } from '../../demo/format'
import { useCampaign } from '../../demo/selectors'
import { demoProducts } from '../../demo/store'
import type { AnalyticsLens, CampaignStatus } from '../../demo/types'
import { Badge, Card, GlossaryText, Icon, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui'
import styles from './TaskTable.module.css'

type BadgeTone = 'green' | 'red' | 'orange' | 'gray' | 'blue'
type AnalyticsRow = Record<string, string | number | null>

interface EnumFixture {
  status: Record<CampaignStatus, string>
  statusTone: Record<CampaignStatus, BadgeTone>
}

interface TaskTableProps {
  rows: AnalyticsRow[]
  lens: AnalyticsLens
  action?: ReactNode
}

interface TaskTableRowProps {
  row: AnalyticsRow
  lens: AnalyticsLens
  onOpen: (id: string) => void
}

const enumFixture = enumsJson as unknown as EnumFixture
const productsById = new Map(demoProducts.map((product) => [product.id, product]))

function delta(value: number | null): string {
  if (value === null) return '—'
  const sign = value >= 0 ? '+' : '−'
  return `${sign}${Math.abs(value * 100).toFixed(0)}%`
}

function TaskTableRow({ row, lens, onOpen }: TaskTableRowProps) {
  const campaignId = String(row.campaignId)
  const campaign = useCampaign(campaignId)
  const product = campaign ? productsById.get(campaign.productId) : undefined
  const deltaPrev = row.deltaPrev as number | null
  const status = row.status as CampaignStatus
  const openFromKeyboard = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onOpen(campaignId)
    }
  }

  return (
    <TableRow
      aria-label={`查看 ${String(row.label)}`}
      clickable
      tabIndex={0}
      onClick={() => onOpen(campaignId)}
      onKeyDown={openFromKeyboard}
    >
      <TableCell>
        <div className={styles.task}>
          <img className={styles.thumb} src={`/${product?.image ?? ''}`} alt={product?.name ?? String(row.label)} />
          <span className={styles.taskLabel}>{String(row.label)}</span>
        </div>
      </TableCell>
      <TableCell><Badge tone={enumFixture.statusTone[status]}>{enumFixture.status[status]}</Badge></TableCell>
      <TableCell>{String(row.platform)}</TableCell>
      <TableCell numeric>{formatInteger(Number(row.adCount))}</TableCell>
      <TableCell numeric>{formatUsd(Number(row.spend))}</TableCell>
      {lens === 'sales' ? (
        <>
          <TableCell numeric>{formatInteger(Number(row.orders))}</TableCell>
          <TableCell numeric>{formatUsd(Number(row.cpa), 2)}</TableCell>
          <TableCell numeric>{formatMultiple(Number(row.roas))}</TableCell>
        </>
      ) : (
        <>
          <TableCell numeric>{formatInteger(Number(row.reach))}</TableCell>
          <TableCell numeric>{formatInteger(Number(row.impressions))}</TableCell>
          <TableCell numeric>{formatUsd(Number(row.cpm), 2)}</TableCell>
        </>
      )}
      <TableCell numeric>
        <span className={deltaPrev === null ? styles.muted : deltaPrev >= 0 ? styles.up : styles.down}>{delta(deltaPrev)}</span>
      </TableCell>
      <TableCell className={styles.chevronCell}><Icon className={styles.chevron} name="chevron-right" size={18} strokeWidth={2} /></TableCell>
    </TableRow>
  )
}

export function TaskTable({ rows, lens, action }: TaskTableProps) {
  const navigate = useNavigate()
  const metricColumns = lens === 'sales'
    ? [copyJson.labels.colOrders, copyJson.labels.colCpa, copyJson.labels.colRoas]
    : [copyJson.labels.colReach, copyJson.labels.colImpressions, copyJson.labels.colCpm]

  return (
    <Card className={styles.card} data-scope="analytics-task-table">
      <div className={styles.header}>
        <h3 className={styles.title}>
          {copyJson.labels.taskTable}
          {' '}<span className={styles.subtitle}>{copyJson.labels.taskTableHint}</span>
        </h3>
        {action}
      </div>
      <Table className={styles.table}>
        <TableHead>
          <TableRow>
            <TableHeader>{copyJson.labels.colTask}</TableHeader>
            <TableHeader>{copyJson.labels.colStatus}</TableHeader>
            <TableHeader>{copyJson.labels.colPlatform}</TableHeader>
            <TableHeader>{copyJson.labels.colAdCount}</TableHeader>
            <TableHeader>{copyJson.labels.colSpend}</TableHeader>
            {metricColumns.map((column) => <TableHeader key={column}><GlossaryText text={column} /></TableHeader>)}
            <TableHeader>{copyJson.labels.colDelta}</TableHeader>
            <TableHeader aria-label="进入详情" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TaskTableRow
              key={String(row.campaignId)}
              row={row}
              lens={lens}
              onOpen={(id) => { void navigate(`/monitor/${id}`) }}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}
